import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

const SIZES = ['S', 'M', 'L', 'XL'];

const emptyForm = {
  name: '',
  description: '',
  price: '',
  images: [''],
  hasSize: false,
  stock: '',                              // used when hasSize = false
  sizeStock: { S: '', M: '', L: '', XL: '' } // used when hasSize = true
};

function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState(emptyForm);
  const [editId, setEditId]     = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [message, setMessage]   = useState('');

  async function loadProducts() {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'products'));
      setProducts(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading products:', err);
    }
    setLoading(false);
  }

  useEffect(() => { loadProducts(); }, []);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  function handleSizeStockChange(size, value) {
    setForm(prev => ({ ...prev, sizeStock: { ...prev.sizeStock, [size]: value } }));
  }

  // ── Image list helpers ──────────────────────────────────────────────────
  function updateImage(index, value) {
    const next = [...form.images];
    next[index] = value;
    setForm(prev => ({ ...prev, images: next }));
  }
  function addImageSlot() {
    setForm(prev => ({ ...prev, images: [...prev.images, ''] }));
  }
  function removeImage(index) {
    const next = form.images.filter((_, i) => i !== index);
    setForm(prev => ({ ...prev, images: next.length ? next : [''] }));
  }
  function moveImage(index, direction) {
    const next = [...form.images];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setForm(prev => ({ ...prev, images: next }));
  }
  // ────────────────────────────────────────────────────────────────────────

  function startAdd() {
    setForm(emptyForm);
    setEditId(null);
    setShowForm(true);
    setMessage('');
  }

  function startEdit(product) {
    const imgs = product.images?.length
      ? product.images
      : (product.imageUrl ? [product.imageUrl] : ['']);

    // Determine if product uses per-size stock
    const hasSz = product.hasSize === true;
    const stockObj = (hasSz && typeof product.stock === 'object' && product.stock !== null)
      ? product.stock
      : { S: '', M: '', L: '', XL: '' };
    const stockNum = (!hasSz && typeof product.stock === 'number') ? String(product.stock) : '';

    setForm({
      name:        product.name        || '',
      description: product.description || '',
      price:       product.price       || '',
      images:      imgs,
      hasSize:     hasSz,
      stock:       stockNum,
      sizeStock: {
        S:  String(stockObj.S  ?? ''),
        M:  String(stockObj.M  ?? ''),
        L:  String(stockObj.L  ?? ''),
        XL: String(stockObj.XL ?? ''),
      }
    });
    setEditId(product.id);
    setShowForm(true);
    setMessage('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);

    const cleanImages = form.images.map(u => u.trim()).filter(Boolean);

    const stockValue = form.hasSize
      ? {
          S:  parseInt(form.sizeStock.S)  || 0,
          M:  parseInt(form.sizeStock.M)  || 0,
          L:  parseInt(form.sizeStock.L)  || 0,
          XL: parseInt(form.sizeStock.XL) || 0,
        }
      : parseInt(form.stock) || 0;

    const productData = {
      name:        form.name.trim(),
      description: form.description.trim(),
      price:       parseFloat(form.price),
      images:      cleanImages,
      imageUrl:    cleanImages[0] || '',
      hasSize:     form.hasSize,
      stock:       stockValue,
      updatedAt:   Timestamp.now()
    };

    try {
      if (editId) {
        await updateDoc(doc(db, 'products', editId), productData);
        setMessage('✅ Product updated successfully!');
      } else {
        productData.createdAt = Timestamp.now();
        await addDoc(collection(db, 'products'), productData);
        setMessage('✅ Product added successfully!');
      }
      setShowForm(false);
      setEditId(null);
      setForm(emptyForm);
      loadProducts();
    } catch (err) {
      setMessage('❌ Error saving product. Please try again.');
      console.error(err);
    }
    setSaving(false);
  }

  async function handleDelete(id, name) {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'products', id));
      setMessage(`✅ "${name}" deleted.`);
      loadProducts();
    } catch (err) {
      setMessage('❌ Error deleting product.');
      console.error(err);
    }
  }

  // Helper: readable stock summary for table
  function stockSummary(product) {
    if (product.hasSize && typeof product.stock === 'object' && product.stock !== null) {
      const total = Object.values(product.stock).reduce((a, b) => (a + (b || 0)), 0);
      const breakdown = SIZES.map(s => `${s}:${product.stock[s] ?? 0}`).join(' ');
      return `${total} total (${breakdown})`;
    }
    return typeof product.stock === 'number' ? String(product.stock) : 'N/A';
  }

  // Helper: badge color based on availability
  function stockBadge(product) {
    if (product.hasSize && typeof product.stock === 'object' && product.stock !== null) {
      const available = Object.values(product.stock).some(v => v > 0);
      const total = Object.values(product.stock).reduce((a, b) => (a + (b || 0)), 0);
      if (!available) return 'bg-danger';
      if (total <= 10) return 'bg-warning text-dark';
      return 'bg-success';
    }
    const s = product.stock ?? 0;
    if (s === 0) return 'bg-danger';
    if (s <= 5) return 'bg-warning text-dark';
    return 'bg-success';
  }

  return (
    <div className="container py-5">

      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <Link to="/admin" className="text-muted small text-decoration-none">← Dashboard</Link>
          <h2 className="fw-bold mb-0">📦 Products</h2>
        </div>
        <button className="btn btn-piggy" onClick={startAdd}>+ Add Product</button>
      </div>

      {message && (
        <div className="alert alert-info alert-dismissible fade show" role="alert">
          {message}
          <button type="button" className="btn-close" onClick={() => setMessage('')} />
        </div>
      )}

      {/* Add / Edit Form */}
      {showForm && (
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-3">{editId ? '✏️ Edit Product' : '➕ Add New Product'}</h5>
            <form onSubmit={handleSave}>
              <div className="row g-3">

                <div className="col-md-6">
                  <label className="form-label">Product Name *</label>
                  <input name="name" className="form-control" value={form.name}
                    onChange={handleChange} placeholder="e.g. Piggy T-Shirt" required />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Price ($) *</label>
                  <input name="price" type="number" step="0.01" min="0" className="form-control"
                    value={form.price} onChange={handleChange} placeholder="19.99" required />
                </div>

                {/* hasSize toggle — takes the last col */}
                <div className="col-md-3 d-flex align-items-end pb-1">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="hasSizeToggle"
                      name="hasSize"
                      checked={form.hasSize}
                      onChange={handleChange}
                    />
                    <label className="form-check-label fw-semibold" htmlFor="hasSizeToggle">
                      Has Sizes (S / M / L / XL)
                    </label>
                  </div>
                </div>

                <div className="col-12">
                  <label className="form-label">Description *</label>
                  <textarea name="description" className="form-control" rows="2"
                    value={form.description} onChange={handleChange}
                    placeholder="A short description of the product..." required />
                </div>

                {/* Stock — conditional on hasSize */}
                <div className="col-12">
                  {form.hasSize ? (
                    <>
                      <label className="form-label fw-semibold">
                        Stock per Size
                        <span className="text-muted fw-normal ms-1 small">— enter 0 if a size is sold out</span>
                      </label>
                      <div className="d-flex gap-3 flex-wrap">
                        {SIZES.map(size => (
                          <div key={size} style={{ width: '80px' }}>
                            <label className="form-label small fw-bold text-uppercase mb-1">{size}</label>
                            <input
                              type="number"
                              min="0"
                              className="form-control form-control-sm text-center"
                              value={form.sizeStock[size]}
                              onChange={e => handleSizeStockChange(size, e.target.value)}
                              placeholder="0"
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div style={{ maxWidth: '180px' }}>
                      <label className="form-label">Stock Quantity</label>
                      <input name="stock" type="number" min="0" className="form-control"
                        value={form.stock} onChange={handleChange} placeholder="100" />
                    </div>
                  )}
                </div>

                {/* Multi-image URLs */}
                <div className="col-12">
                  <label className="form-label fw-semibold">
                    Images
                    <span className="text-muted fw-normal ms-1 small">— first image is the main product photo</span>
                  </label>
                  <div className="d-flex flex-column gap-2">
                    {form.images.map((url, i) => (
                      <div key={i} className="d-flex align-items-center gap-2">
                        <div className="d-flex flex-column gap-1" style={{ flexShrink: 0 }}>
                          <button type="button" className="btn btn-outline-secondary btn-sm p-0"
                            style={{ width: '24px', height: '24px', lineHeight: 1, fontSize: '0.7rem' }}
                            onClick={() => moveImage(i, -1)} disabled={i === 0} title="Move up">▲</button>
                          <button type="button" className="btn btn-outline-secondary btn-sm p-0"
                            style={{ width: '24px', height: '24px', lineHeight: 1, fontSize: '0.7rem' }}
                            onClick={() => moveImage(i, 1)} disabled={i === form.images.length - 1} title="Move down">▼</button>
                        </div>
                        {i === 0 && (
                          <span className="badge text-uppercase"
                            style={{ background: '#111', color: 'white', fontSize: '0.65rem', letterSpacing: '0.05em', flexShrink: 0 }}>
                            Main
                          </span>
                        )}
                        {url.trim() && (
                          <img src={url.trim()} alt={`Preview ${i + 1}`}
                            style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '2px', border: '1px solid #ddd', flexShrink: 0 }}
                            onError={e => { e.target.style.display = 'none'; }} />
                        )}
                        <input type="url" className="form-control" value={url}
                          onChange={e => updateImage(i, e.target.value)}
                          placeholder={i === 0 ? 'Main image URL (front view)' : `Image ${i + 1} URL`} />
                        {form.images.length > 1 && (
                          <button type="button" className="btn btn-outline-danger btn-sm"
                            onClick={() => removeImage(i)} style={{ flexShrink: 0 }} title="Remove">×</button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button type="button" className="btn btn-outline-secondary btn-sm mt-2" onClick={addImageSlot}>
                    + Add Another Image
                  </button>
                  <div className="form-text">Leave blank to use the default placeholder image.</div>
                </div>

              </div>

              <div className="mt-3 d-flex gap-2">
                <button type="submit" className="btn btn-piggy" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Product'}
                </button>
                <button type="button" className="btn btn-outline-secondary"
                  onClick={() => { setShowForm(false); setForm(emptyForm); }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Products Table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: 'var(--piggy-pink)' }} role="status" />
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Product</th>
                <th>Price</th>
                <th>Type</th>
                <th>Stock</th>
                <th>Images</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-5">
                    No products yet. Click "Add Product" to get started!
                  </td>
                </tr>
              ) : (
                products.map(product => {
                  const thumb    = product.images?.[0] || product.imageUrl || null;
                  const imgCount = product.images?.length || (product.imageUrl ? 1 : 0);
                  return (
                    <tr key={product.id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {thumb && (
                            <img src={thumb} alt={product.name}
                              style={{ width: '45px', height: '45px', objectFit: 'cover' }}
                              className="rounded" />
                          )}
                          <span className="fw-semibold">{product.name}</span>
                        </div>
                      </td>
                      <td>${product.price?.toFixed(2)}</td>
                      <td>
                        <span className="badge bg-secondary">
                          {product.hasSize ? 'Sized' : 'One-size'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${stockBadge(product)} me-1`}>
                          {product.hasSize ? '●' : (product.stock ?? 'N/A')}
                        </span>
                        <span className="small text-muted" style={{ fontSize: '0.75rem' }}>
                          {stockSummary(product)}
                        </span>
                      </td>
                      <td className="small text-muted">
                        {imgCount > 0 ? `${imgCount} image${imgCount !== 1 ? 's' : ''}` : '—'}
                      </td>
                      <td>
                        <button className="btn btn-outline-primary btn-sm me-2" onClick={() => startEdit(product)}>Edit</button>
                        <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(product.id, product.name)}>Delete</button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminProducts;
